import { fetchData } from 'modules/Axios';
import { notifyFailure } from 'modules/Notifications';
import { sortByPropAscendingCaseInsensitive } from 'lib/Sorting';

import attachLocationsDataToSegment from '../utils/attachLocationsDataToSegment';

export const defaultState = {
  categories: [],
  currentTab: 'SUMMARY',
  locations: [],
  currentLocationPage: 0,
  locationSearchValue: '',
  items: [],
  segmentData: { locationData: [], metaData: { filters: [], locations: {} }, stats: {} },
  segmentId: '',
  loading: false,
  summaryLoading: false,
};

export const TOGGLE_CURRENT_DETAILS_TAB = 'TOGGLE_CURRENT_DETAILS_TAB';
/**
 * Toggles Current Active Tab
 * @function toggleDetailsTab
 * @param {string} tab
 * @return {Object} TOGGLE_CURRENT_DETAILS_TAB Payload
 */
export const toggleDetailsTab = tab => ({
  type: TOGGLE_CURRENT_DETAILS_TAB,
  payload: tab,
});

export const SEGMENT_DETAILS_LOADING = 'SEGMENT_DETAILS_LOADING';
/**
 * Toggles Segment Details Loading Flag
 * @function segmentDetailsLoading
 * @param {boolean} bool
 * @return {Object} SEGMENT_DETAILS_LOADING Payload
 */
export const segmentDetailsLoading = bool => ({
  type: SEGMENT_DETAILS_LOADING,
  payload: bool,
});

export const SEGMENT_DETAILS_SUMMARY_LOADING = 'SEGMENT_DETAILS_SUMMARY_LOADING';
/**
 * Toggles Segment Details Summary Loading Flag
 * @function segmentDetailsSummaryLoading
 * @param {boolean} bool
 * @return {Object} SEGMENT_DETAILS_SUMMARY_LOADING Payload
 */
export const summaryLoading = bool => ({
  type: SEGMENT_DETAILS_SUMMARY_LOADING,
  payload: bool,
});

export const RECEIVE_SEGMENT_DETAILS = 'RECEIVE_SEGMENT_DETAILS';
/**
 * Accepts Segment Data & Updates State
 * @function receiveSegmentDetails
 * @param {Object} segment - Returned Segment Data
 * @param {number} segmentId
 * @return {Object} RECEIVE_SEGMENT_DETAILS Payload
 */
export const receiveSegmentDetails = (segment, segmentId) => ({
  type: RECEIVE_SEGMENT_DETAILS,
  payload: {
    segment,
    segmentId,
  },
});

/**
 * Fetch Segment Data
 * @function fetchSegmentDetails
 * @param {number} id
 * @return {Function} Dispatch of `receiveSegmentDetails`
 */
export const fetchSegmentDetails = id => {
  return async (dispatch, getState) => {
    dispatch(segmentDetailsLoading(true));
    const { brand } = getState().app;

    const segment = await dispatch(fetchData(`/brands/${brand.id}/segments/${id}/verbose`))
      .catch(e => {
        notifyFailure('Failed to retrieve segment details');
        dispatch(segmentDetailsLoading(false));
        return { ...defaultState.segmentData };
      });
    await dispatch(fetchLocations(segment.metaData.id));
    /* Pull Newly Fetched Locations & Feed to attachLocationsDataToSegment Util Function */
    const { locations } = getState().segmentDetails;
    return dispatch(receiveSegmentDetails(attachLocationsDataToSegment({ segment, locations }), id));
  };
};

/**
 * Fetches Locations from `/sites` Endpoint
 * @function fetchLocations in Customer Module
 * @description Fetch Brand Sites
 * @return {Function} Dispatch of RECEIVE_LOCATIONS Payload
 */
export const fetchLocations = (id, shouldUpdateGrid = false, searchField = 'all', searchValue = '') => {
  return async (dispatch, getState) => {
    const { brand } = getState().app;
    const { currentLocationPage, locations } = getState().segmentDetails;
    const locationsData = await dispatch(fetchData(`brands/${brand.id}/segments/${id}/locations?searchFields=name,storeId,city,state,postalCode&searchValue=&size=10000000&page=0&sort=name,DESC`))
      .catch(e => {
        console.error('Could not fetch brand locations: ', e);
        return [];
      });
    await dispatch(receiveLocations(locationsData.content));
  };
};

export const UPDATE_SEGMENT_DETAILS_LOCATIONS_PAGE = 'UPDATE_SEGMENT_DETAILS_LOCATIONS_PAGE';
export const updateSegmentDetailsLocationsPage = page => ({
  type: UPDATE_SEGMENT_DETAILS_LOCATIONS_PAGE,
  payload: page,
});

export const RECEIVE_LOCATIONS = 'SEGMENT_DETAILS_RECEIVE_LOCATIONS_CUSTOMERS';
/**
 * @function receiveLocations in SegmentDetails Module
 * @param {Array.<Object>} locations
 * @return {Object} RECEIVE_CUSTOMERS Payload
 */
export const receiveLocations = locations => ({
  type: RECEIVE_LOCATIONS,
  payload: locations,
});

export const RESET_SEGMENT_DETAILS_ROUTE = 'RESET_SEGMENT_DETAILS_ROUTE';
/**
 * Resets Segment Details Route to Initial State
 * @function resetSegmentDetailsRoute
 * @return {Object} RESET_SEGMENT_DETAILS_ROUTE Payload
 */
export const resetSegmentDetailsRoute = () => ({
  type: RESET_SEGMENT_DETAILS_ROUTE,
});

export default function reducer(state = defaultState, action = {}) {
  switch (action.type) {
    case RECEIVE_LOCATIONS:
      return {
        ...state,
        locations: sortByPropAscendingCaseInsensitive(action.payload, 'city'),
      };
    case RECEIVE_SEGMENT_DETAILS:
      return {
        ...state,
        loading: false,
        segmentData: { ...action.payload.segment },
        segmentId: action.payload.segmentId,
      };
    case RESET_SEGMENT_DETAILS_ROUTE:
      return { ...defaultState };
    case UPDATE_SEGMENT_DETAILS_LOCATIONS_PAGE:
      return { ...state, currentLocationPage: action.payload };
    case SEGMENT_DETAILS_LOADING:
      return { ...state, loading: action.payload };
    case SEGMENT_DETAILS_SUMMARY_LOADING:
      return { ...state, summaryLoading: action.payload };
    case TOGGLE_CURRENT_DETAILS_TAB:
      return { ...state, currentTab: action.payload };
    default:
      return state;
  }
}
